// client/src/components/communication/VideoSessionOverlay.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect, useRef } from 'react';
import { 
  StreamVideoClient,
  StreamCall,
  StreamCallProvider,   
  SpeakerLayout,
  CallControls,
  ParticipantView,
  useCall,
  useCallStateHooks
} from '@stream-io/video-react-sdk';
import '@stream-io/video-react-sdk/dist/css/styles.css';
import '../../../styles/components/communication/VideoSessionOverlay.css';

const VideoSessionOverlay = ({
  sessionId,
  userRole = 'patient',
  patientInfo,
  doctorInfo,
  onEndSession,
  onUpdateStatus,
  onMinimize = null
}) => {
  // Video call state
  const [streamVideoClient, setStreamVideoClient] = useState(null);
  const [call, setCall] = useState(null);
  const [isConnecting, setIsConnecting] = useState(true);
  const [connectionError, setConnectionError] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  
  // Session timer
  const sessionStartTime = useRef(new Date());
  const timerInterval = useRef(null);

  // ✅ FIXED: Move user ID logic OUTSIDE of useEffect so it's available everywhere
  const getAuthenticatedUserId = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user._id || user.id; // Same logic as mobile app
      }
      return null;
    } catch (error) {
      console.error('Error getting authenticated user ID:', error);
      return null;
    }
  };

  const authenticatedUserId = getAuthenticatedUserId();
  const currentUserId = authenticatedUserId; // Use authenticated user ID
  const otherUserId = userRole === 'doctor' ? patientInfo._id : doctorInfo._id;

  // ✅ COMPLETELY FIXED: Initialize Stream Video client and call
  useEffect(() => {
    const initializeVideoCall = async () => {
      try {
        console.log('🎥 =====================================');
        console.log('🎥 INITIALIZING VIDEO CALL WITH DEBUG');
        console.log('🎥 =====================================');
        
        setIsConnecting(true);
        setConnectionError(null);

        // Add validation
        if (!currentUserId) {
          throw new Error('Unable to get authenticated user ID. Please log in again.');
        }

        console.log('🔍 === FIXED USER ID DEBUG (WEB) ===');
        console.log('🎭 User Role:', userRole);
        console.log('👤 Authenticated User ID:', authenticatedUserId);
        console.log('👥 Other User ID:', otherUserId);
        console.log('📞 Session ID:', sessionId);
        console.log('🆔 Call ID will be:', `video_${sessionId}`);
        console.log('========================');

        // ✅ STEP 1: Request camera permissions FIRST
        console.log('📷 Requesting camera and microphone permissions...');
        
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30 }
            }, 
            audio: true 
          });
          
          console.log('✅ Camera permissions granted, stream:', stream);
          console.log('📊 Video tracks:', stream.getVideoTracks().length);
          console.log('🎵 Audio tracks:', stream.getAudioTracks().length);
          
          // Stop the test stream immediately
          stream.getTracks().forEach(track => {
            track.stop();
            console.log('🛑 Stopped track:', track.kind);
          });
          
        } catch (permissionError) {
          console.error('❌ Camera permission error:', permissionError);
          throw new Error(`Camera permission denied: ${permissionError.message}`);
        }

        // ✅ STEP 2: Get Stream token
        console.log('🔑 Getting Stream token...');
        const token = await getStreamToken();
        if (!token) {
          throw new Error('Failed to get authentication token');
        }
        console.log('✅ Token obtained successfully');

        // ✅ STEP 3: Initialize StreamVideoClient
        const apiKey = '9v6xtvf6dhtv';
        
        console.log('🔧 Initializing StreamVideoClient...');
        console.log('🔑 API Key:', apiKey);
        console.log('👤 User for client:', {
          id: currentUserId,
          name: userRole === 'doctor' 
            ? `Dr. ${doctorInfo.firstName} ${doctorInfo.lastName}`
            : `${patientInfo.firstName} ${patientInfo.lastName}`,
          image: userRole === 'doctor' ? doctorInfo.profilePicture : patientInfo.profilePicture
        });
        
        const streamVideo = new StreamVideoClient({
          apiKey,
          user: {
            id: currentUserId,
            name: userRole === 'doctor' 
              ? `Dr. ${doctorInfo.firstName} ${doctorInfo.lastName}`
              : `${patientInfo.firstName} ${patientInfo.lastName}`,
            image: userRole === 'doctor' ? doctorInfo.profilePicture : patientInfo.profilePicture
          },
          token
        });

        setStreamVideoClient(streamVideo);
        console.log('✅ StreamVideoClient initialized');

        // ✅ STEP 4: Create call with consistent ID
        const callId = `video_${sessionId}`;
        console.log('📞 Creating call with ID:', callId);
        
        const videoCall = streamVideo.call('default', callId);
        console.log('📞 Call object created:', videoCall);

        // ✅ STEP 5: Prepare member list
        const memberList = [
          { user_id: currentUserId },
          { user_id: otherUserId }
        ];
        
        console.log('🔍 === CALL SETUP DEBUG ===');
        console.log('👥 Member List:', memberList);
        console.log('🏗️ Call Create Options:', {
          create: true,
          data: {
            members: memberList,
            settings: {
              audio: { 
                mic_default_on: true,
                noise_cancellation: true
              },
              video: { 
                camera_default_on: true,
                target_resolution: '720p'
              }
            },
            custom: {
              session_id: sessionId,
              user_role: userRole,
              appointment_type: 'therapy_session'
            }
          }
        });
        console.log('========================');

        // ✅ STEP 6: Join call with proper settings
        console.log('🚀 Joining call...');
        
        await videoCall.join({
          create: true,
          data: {
            members: memberList,
            settings: {
              audio: { 
                mic_default_on: true,
                noise_cancellation: true
              },
              video: { 
                camera_default_on: true,
                target_resolution: '720p'
              }
            },
            custom: {
              session_id: sessionId,
              user_role: userRole,
              appointment_type: 'therapy_session'
            }
          }
        });

        setCall(videoCall);
        console.log('✅ Successfully joined video call');

        // ✅ STEP 7: Debug call state after joining
        console.log('🔍 === POST-JOIN DEBUG ===');
        console.log('📊 Call State:', videoCall.state);
        console.log('👥 Call Members:', videoCall.state.members);
        console.log('📋 Call Participants:', videoCall.state.participants);
        console.log('🔗 Call ID:', videoCall.id);
        console.log('🎬 Call Type:', videoCall.type);
        console.log('========================');

        // ✅ STEP 8: Enable camera and microphone AFTER joining
        try {
          console.log('🎬 Enabling camera and microphone...');
          
          await videoCall.camera.enable();
          console.log('✅ Camera enabled');
          
          await videoCall.microphone.enable();
          console.log('✅ Microphone enabled');
          
          setIsVideoEnabled(true);
          setIsAudioEnabled(true);
          
          console.log('✅ All media devices enabled successfully');
          
        } catch (enableError) {
          console.error('⚠️ Error enabling camera/mic (continuing anyway):', enableError);
        }

        // ✅ STEP 9: Set up call listeners
        try {
          console.log('👂 Setting up call event listeners...');
          
          videoCall.on('call.session_participant_joined', (event) => {
            console.log('🎉 PARTICIPANT JOINED EVENT:', event);
            console.log('👤 Participant details:', event.participant);
          });

          videoCall.on('call.session_participant_left', (event) => {
            console.log('👋 PARTICIPANT LEFT EVENT:', event);
            console.log('👤 Participant details:', event.participant);
          });

          videoCall.on('call.session_started', (event) => {
            console.log('🚀 CALL SESSION STARTED:', event);
          });

          videoCall.on('call.session_ended', (event) => {
            console.log('🏁 CALL SESSION ENDED:', event);
          });
          
          console.log('✅ Event listeners set up successfully');
          
        } catch (listenerError) {
          console.error('⚠️ Error setting up event listeners (continuing anyway):', listenerError);
        }

        // ✅ STEP 10: Update session status
        if (onUpdateStatus) {
          console.log('📡 Updating session status to active...');
          await onUpdateStatus(sessionId, 'active');
        }

        // ✅ STEP 11: Start session timer
        startSessionTimer();

        console.log('🎉 =====================================');
        console.log('🎉 VIDEO CALL INITIALIZATION COMPLETE!');
        console.log('🎉 =====================================');

      } catch (error) {
        console.error('❌ =====================================');
        console.error('❌ ERROR INITIALIZING VIDEO CALL:', error);
        console.error('❌ Error Stack:', error.stack);
        console.error('❌ =====================================');
        setConnectionError(error.message || 'Failed to join video session');
      } finally {
        setIsConnecting(false);
      }
    };

    initializeVideoCall();

    return () => {
      cleanup();
    };
  }, [sessionId, userRole, currentUserId, otherUserId]); // Add dependencies

  // ✅ ENHANCED: Get Stream token with debug logging
  const getStreamToken = async () => {
    try {
      console.log('🔍 Getting Stream token...');
      
      const requestBody = { action: 'generateToken' };
      const authToken = localStorage.getItem('token');
      
      console.log('📤 Token Request Details:');
      console.log('  - URL: /api/chat/token');
      console.log('  - Method: POST');
      console.log('  - Body:', requestBody);
      console.log('  - Auth Token Present:', !!authToken);
      console.log('  - Auth Token Length:', authToken ? authToken.length : 0);
      
      const response = await fetch('/api/chat/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('📥 Token Response Status:', response.status);
      console.log('📥 Token Response OK:', response.ok);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Token Response Data:', result);
        
        if (result.success && result.data?.token) {
          localStorage.setItem('streamChatToken', result.data.token);
          console.log('✅ Stream token obtained and stored');
          console.log('🔑 Token Length:', result.data.token.length);
          return result.data.token;
        } else {
          console.error('❌ Token response missing data:', result);
          throw new Error('Invalid token response from server');
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Token request failed:', response.status, errorText);
        throw new Error(`Token request failed: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error getting Stream token:', error);
      throw error;
    }
  };

  // Start session timer
  const startSessionTimer = () => {
    sessionStartTime.current = new Date();
    timerInterval.current = setInterval(() => {
      const now = new Date();
      const duration = Math.floor((now - sessionStartTime.current) / 1000);
      setSessionDuration(duration);
    }, 1000);
  };

  // Format session duration
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Toggle video
  const toggleVideo = async () => {
    if (!call) return;
    
    try {
      console.log('🎬 Toggling video, current state:', isVideoEnabled);
      
      if (isVideoEnabled) {
        await call.camera.disable();
        console.log('📷 Camera disabled');
      } else {
        await call.camera.enable();
        console.log('📷 Camera enabled');
      }
      setIsVideoEnabled(!isVideoEnabled);
    } catch (error) {
      console.error('❌ Error toggling video:', error);
    }
  };

  // Toggle audio
  const toggleAudio = async () => {
    if (!call) return;
    
    try {
      console.log('🎵 Toggling audio, current state:', isAudioEnabled);
      
      if (isAudioEnabled) {
        await call.microphone.disable();
        console.log('🎤 Microphone disabled');
      } else {
        await call.microphone.enable();
        console.log('🎤 Microphone enabled');
      }
      setIsAudioEnabled(!isAudioEnabled);
    } catch (error) {
      console.error('❌ Error toggling audio:', error);
    }
  };

  // Switch camera
  const switchCamera = async () => {
    if (!call) return;
    
    try {
      await call.camera.flip();
      console.log('🔄 Camera switched');
    } catch (error) {
      console.error('❌ Error switching camera:', error);
    }
  };

  // ✅ FIXED: End session function
  const handleEndSession = async () => {
    console.log('🔴 handleEndSession called, showEndConfirm:', showEndConfirm);
    
    if (isEnding) {
      console.log('⏳ Already ending session, ignoring...');
      return;
    }
    
    if (!showEndConfirm) {
      console.log('📝 Showing confirmation dialog...');
      setShowEndConfirm(true);
      return;
    }

    try {
      console.log('🎥 STARTING END SESSION PROCESS...');
      setIsEnding(true);
      setShowEndConfirm(false);
      
      if (call) {
        console.log('📞 Leaving call...');
        await call.leave();
        console.log('✅ Call left successfully');
      }
      
      cleanup();
      
      if (onUpdateStatus) {
        onUpdateStatus(sessionId, 'ended').catch(console.error);
      }
      
      if (onEndSession) {
        console.log('📞 Calling onEndSession...');
        onEndSession();
      }
      
    } catch (error) {
      console.error('❌ Error ending session:', error);
    } finally {
      setIsEnding(false);
    }
  };

  // ✅ FIXED: Force end session (for modal button)
  const forceEndSession = async () => {
    console.log('🔴 FORCE END SESSION CALLED');
    
    try {
      setIsEnding(true);
      setShowEndConfirm(false);
      
      // Force cleanup
      if (call) {
        console.log('🔴 Force leaving call...');
        await call.leave().catch(console.error);
      }
      
      cleanup();
      
      // Force update status
      if (onUpdateStatus) {
        onUpdateStatus(sessionId, 'ended').catch(console.error);
      }
      
      // Force call parent callback
      if (onEndSession) {
        console.log('🔴 Force calling onEndSession...');
        onEndSession();
      } else {
        console.log('🔴 No onEndSession callback, reloading page...');
        window.location.reload();
      }
      
    } catch (error) {
      console.error('❌ Error in force end session:', error);
      // Force reload as fallback
      window.location.reload();
    }
  };

  // Cleanup function
  const cleanup = () => {
    console.log('🧹 Cleaning up video session...');
    
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }
    
    if (call) {
      call.leave().catch(console.error);
    }
    
    if (streamVideoClient) {
      streamVideoClient.disconnectUser().catch(console.error);
    }
  };

  // Minimize/maximize overlay
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    if (onMinimize) {
      onMinimize(!isMinimized);
    }
  };

  // Get participant info for display
  const getOtherParticipant = () => {
    return userRole === 'doctor' ? patientInfo : doctorInfo;
  };

  const otherParticipant = getOtherParticipant();

  // Minimized view
  if (isMinimized) {
    return (
      <div className="video-session-minimized">
        <div className="minimized-content">
          <div className="minimized-info">
            <div className="session-indicator">
              <div className="live-dot"></div>
              <span>Live Session</span>
            </div>
            <div className="participant-name">
              {userRole === 'doctor' 
                ? `${otherParticipant.firstName} ${otherParticipant.lastName}`
                : `Dr. ${otherParticipant.firstName} ${otherParticipant.lastName}`
              }
            </div>
            <div className="session-timer">{formatDuration(sessionDuration)}</div>
          </div>
          <div className="minimized-controls">
            <button
              className={`control-btn ${isAudioEnabled ? 'enabled' : 'disabled'}`}
              onClick={toggleAudio}
              title={isAudioEnabled ? 'Mute' : 'Unmute'}
            >
              {isAudioEnabled ? '🎤' : '🔇'}
            </button>
            <button
              className={`control-btn ${isVideoEnabled ? 'enabled' : 'disabled'}`}
              onClick={toggleVideo}
              title={isVideoEnabled ? 'Turn Off Camera' : 'Turn On Camera'}
            >
              {isVideoEnabled ? '📹' : '📷'}
            </button>
            <button
              className="control-btn maximize"
              onClick={toggleMinimize}
              title="Maximize"
            >
              ⬆️
            </button>
            <button
              className="control-btn end-call"
              onClick={() => setShowEndConfirm(true)}
              title="End Session"
            >
              📞
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="video-session-overlay">
      {/* ✅ FIXED Debug Info Panel */}
      <div style={{
        position: 'absolute',
        top: '50px',
        left: '10px',
        background: 'rgba(0,0,0,0.8)',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        fontSize: '12px',
        zIndex: 1000,
        maxWidth: '300px'
      }}>
        <div><strong>🔍 DEBUG INFO (WEB):</strong></div>
        <div>Role: {userRole}</div>
        <div>Session: {sessionId}</div>
        <div>Call ID: video_{sessionId}</div>
        <div>Current User: {currentUserId}</div>
        <div>Other User: {otherUserId}</div>
        <div>Call State: {call ? 'Connected' : 'Not Connected'}</div>
        <div>Participants: {call?.state?.participants?.length || 0}</div>
        <div>Video: {isVideoEnabled ? 'ON' : 'OFF'}</div>
        <div>Audio: {isAudioEnabled ? 'ON' : 'OFF'}</div>
      </div>

      {/* Session Header */}
      <div className="session-header">
        <div className="session-info">
          <div className="session-title">
            <h3>
              {userRole === 'doctor' 
                ? `Session with ${otherParticipant.firstName} ${otherParticipant.lastName}`
                : `Session with Dr. ${otherParticipant.firstName} ${otherParticipant.lastName}`
              }
            </h3>
            <div className="session-status">
              <div className="live-indicator">
                <div className="live-dot"></div>
                <span>Live Session</span>
              </div>
              <div className="session-duration">{formatDuration(sessionDuration)}</div>
            </div>
          </div>
        </div>
        
        <div className="header-controls">
          {onMinimize && (
            <button
              className="control-btn minimize"
              onClick={toggleMinimize}
              title="Minimize"
            >
              ⬇️
            </button>
          )}
          <button
            className="control-btn close"
            onClick={() => setShowEndConfirm(true)}
            title="End Session"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Video Content */}
      <div className="video-content">
        {isConnecting ? (
          <div className="connecting-state">
            <div className="connecting-spinner"></div>
            <h3>Connecting to video session...</h3>
            <p>Please wait while we establish your secure connection.</p>
            <div className="connection-details">
              <div className="detail-item">
                <span className="detail-label">Session ID:</span>
                <span className="detail-value">{sessionId.split('_')[1]?.substring(0, 8) || 'Unknown'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">User Role:</span>
                <span className="detail-value">{userRole}</span>
              </div>
            </div>
          </div>
        ) : connectionError ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h3>Connection Error</h3>
            <p>{connectionError}</p>
            <div className="error-details">
              <details>
                <summary>Technical Details</summary>
                <div className="tech-details">
                  <p><strong>Session ID:</strong> {sessionId}</p>
                  <p><strong>User Role:</strong> {userRole}</p>
                  <p><strong>Timestamp:</strong> {new Date().toISOString()}</p>
                  <p><strong>Error:</strong> {connectionError}</p>
                </div>
              </details>
            </div>
            <div className="error-actions">
              <button 
                className="retry-btn"
                onClick={() => window.location.reload()}
              >
                Retry Connection
              </button>
              <button 
                className="cancel-btn"
                onClick={onEndSession}
              >
                Cancel Session
              </button>
            </div>
          </div>
        ) : call ? (
          <StreamCallProvider call={call}>
            <div className="stream-video-container">
              <SpeakerLayout />
            </div>
          </StreamCallProvider>
        ) : (
          <div className="no-call-state">
            <div className="no-call-icon">📹</div>
            <h3>Video session not available</h3>
            <p>Unable to establish video connection.</p>
          </div>
        )}
      </div>

      {/* Control Bar */}
      <div className="control-bar">
        <div className="control-group">
          <button
            className={`control-btn large ${isAudioEnabled ? 'enabled' : 'disabled'}`}
            onClick={toggleAudio}
            title={isAudioEnabled ? 'Mute Microphone' : 'Unmute Microphone'}
          >
            <span className="control-icon">
              {isAudioEnabled ? '🎤' : '🔇'}
            </span>
            <span className="control-label">
              {isAudioEnabled ? 'Mute' : 'Unmute'}
            </span>
          </button>

          <button
            className={`control-btn large ${isVideoEnabled ? 'enabled' : 'disabled'}`}
            onClick={toggleVideo}
            title={isVideoEnabled ? 'Turn Off Camera' : 'Turn On Camera'}
          >
            <span className="control-icon">
              {isVideoEnabled ? '📹' : '📷'}
            </span>
            <span className="control-label">
              {isVideoEnabled ? 'Camera On' : 'Camera Off'}
            </span>
          </button>

          <button
            className="control-btn large"
            onClick={switchCamera}
            title="Switch Camera"
          >
            <span className="control-icon">🔄</span>
            <span className="control-label">Switch</span>
          </button>
        </div>

        <div className="control-group">
          <button
            className="control-btn large end-call"
            onClick={() => {
              console.log('🔴 FORCE END BUTTON CLICKED');
              console.log('🔴 isEnding state:', isEnding);
              console.log('🔴 showEndConfirm state:', showEndConfirm);
              
              // Force show confirmation
              setShowEndConfirm(true);
            }}
            title="End Session"
            disabled={false}
            style={{
              opacity: 1,
              cursor: 'pointer'
            }}
          >
            <span className="control-icon">📞</span>
            <span className="control-label">End Session</span>
          </button>
        </div>
      </div>

      {/* ✅ FIXED: End Session Modal */}
      {showEndConfirm && (
        <div 
          className="end-session-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 999999,
            pointerEvents: 'auto'
          }}
          onClick={(e) => {
            console.log('🔍 Modal background clicked');
            e.stopPropagation();
            // Only close if clicking the backdrop, not the modal content
            if (e.target === e.currentTarget) {
              setShowEndConfirm(false);
            }
          }}
        >
          <div 
            className="modal-content"
            style={{
              position: 'relative',
              zIndex: 1000000,
              pointerEvents: 'auto'
            }}
            onClick={(e) => {
              console.log('🔍 Modal content clicked');
              e.stopPropagation(); // Prevent closing when clicking modal content
            }}
          >
            <div className="modal-header">
              <h3>End Video Session?</h3>
              <button 
                className="modal-close"
                onClick={(e) => {
                  console.log('🔍 Close button clicked');
                  e.stopPropagation();
                  setShowEndConfirm(false);
                }}
                style={{
                  pointerEvents: 'auto',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to end this therapy session?</p>
              <div className="session-summary">
                <div className="summary-item">
                  <span className="summary-label">Duration:</span>
                  <span className="summary-value">{formatDuration(sessionDuration)}</span>
                </div>
              </div>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            
            <div 
              className="modal-actions"
              style={{
                pointerEvents: 'auto',
                zIndex: 1000001
              }}
            >
              <button
                className="btn secondary"
                onClick={(e) => {
                  console.log('🔍 Cancel button clicked');
                  e.stopPropagation();
                  setShowEndConfirm(false);
                }}
                disabled={isEnding}
                style={{
                  pointerEvents: 'auto',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              
              <button
                className="btn primary end"
                onClick={(e) => {
                  console.log('🔴 END SESSION MODAL BUTTON CLICKED!');
                  console.log('🔴 Event object:', e);
                  e.stopPropagation();
                  e.preventDefault();
                  
                  // Force end immediately
                  forceEndSession();
                }}
                disabled={false}
                style={{
                  pointerEvents: 'auto',
                  cursor: 'pointer',
                  opacity: 1,
                  backgroundColor: '#ef4444',
                  color: 'white',
                  zIndex: 1000002
                }}
                onMouseDown={(e) => {
                  console.log('🔴 Mouse down on end button');
                  e.stopPropagation();
                }}
                onMouseUp={(e) => {
                  console.log('🔴 Mouse up on end button');
                  e.stopPropagation();
                }}
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoSessionOverlay;